/**
 * OPLYX — Emissão de NFC-e a partir do PDV / Comandas.
 *
 * Quem emite é o backend, pelo emissor próprio (SEFAZ direta). Se a SEFAZ cair,
 * o próprio backend entra em contingência offline e a nota sai do mesmo jeito.
 * Aqui só existe um caso a tratar: o PDV não alcança o backend. Aí a venda vai
 * para a fila local (IndexedDB) e é enviada quando a conexão volta —
 * ver useNfceSyncQueue em lib/connectivity.js.
 *
 * A emissão é assíncrona: a resposta do POST raramente já traz a nota. Quem
 * chama acompanha por `fetchNfceBySale` até o status sair de "pending".
 */
import { api } from "@/lib/api";
import { queueNfce } from "@/lib/offline_db";

/** Status em que a nota ainda pode mudar sozinha — vale continuar consultando. */
export const NFCE_EM_ANDAMENTO = new Set(["pending"]);

/** Status com cupom disponível para impressão. */
export const NFCE_IMPRIMIVEL = new Set(["authorized", "contingency"]);

/**
 * Pede a emissão da NFC-e de uma venda.
 *
 * @returns {Promise<{success: boolean, source: "online"|"offline_queue", document?: object, queued?: boolean}>}
 */
export async function emitNfce({ saleId, onStatusChange }) {
  try {
    const { data } = await api.post("/fiscal/nfce", { sale_id: saleId }, { timeout: 12000 });
    onStatusChange?.("online");
    return { success: true, source: "online", document: data.document || null };
  } catch (e) {
    const isNetworkErr = !e?.response?.status;
    if (!isNetworkErr) throw e; // erro HTTP (config, venda cancelada…) vai para o caller
  }

  await queueNfce({
    idempotency_key: saleId,
    sale_id:         saleId,
    queued_at:       new Date().toISOString(),
    status:          "PENDENTE_SYNC",
  });
  onStatusChange?.("queued");
  return { success: false, source: "offline_queue", queued: true };
}

/**
 * NFC-e mais recente da venda, já no formato da tela.
 *
 * Sem documento ainda, devolve um documento sintético a partir do que a venda
 * sabe — "pending" enquanto emite, "error" com o motivo quando não saiu.
 */
export async function fetchNfceBySale(saleId) {
  const { data } = await api.get(`/fiscal/nfce/by-sale/${saleId}`);
  if (data.document) return data.document;
  if (data.sale_nfce_status === "error" || data.sale_nfce_status === "rejected") {
    return { status: "error", rejection_reason: data.sale_nfce_error || "NFC-e não emitida", sale_id: saleId };
  }
  return { status: "pending", sale_id: saleId };
}

/** Abre o cupom da NFC-e numa janela que já chama o diálogo de impressão. */
export function openNfceCupom(docId, windowName = "_blank", features) {
  const token = localStorage.getItem("oplyx_token") || "";
  window.open(
    `${process.env.REACT_APP_BACKEND_URL}/api/fiscal/nfce/doc/${docId}/pdf?t=${encodeURIComponent(token)}`,
    windowName,
    features,
  );
}
